import React, {FC, useMemo, useState} from "react";
import {Button} from "antd";
import {LeftOutlined, RightOutlined} from "@ant-design/icons";
import {
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    isSameMonth,
    isSameDay,
    addMonths,
    subMonths,
    getDate,
} from "date-fns";
import {AssistantLessonViewProps} from "./AssistantLargeTiles";
import {getLessonsForDay, formatTimeRange} from "./assistantDateUtils";
import formatDate from "../../helpers/formatDate";
import styles from "./assistantApp.less";

const DAY_NAMES = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

type AssistantMonthCalendarProps = AssistantLessonViewProps & {
    monthDate: Date;
    onMonthChange: (newMonthDate: Date) => void;
};

/**
 * Month calendar view for the assistant app.
 * Displays a traditional month grid where each day cell shows a dot indicator
 * when lessons exist. Tapping a day reveals the lessons below the calendar
 * as a compact list. Supports month-by-month navigation.
 */
const AssistantMonthCalendar: FC<AssistantMonthCalendarProps> = ({
    lessons,
    pendingLessonId,
    onAssign,
    onUnassign,
    monthDate,
    onMonthChange,
}) => {
    const [selectedDay, setSelectedDay] = useState<Date>(() => {
        const today = new Date();
        if (isSameMonth(today, monthDate)) return today;
        return startOfMonth(monthDate);
    });

    const today = new Date();
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);

    // Build the full calendar grid: start from Monday of the week containing the 1st
    const calendarStart = startOfWeek(monthStart, {weekStartsOn: 1});
    const calendarEnd = endOfWeek(monthEnd, {weekStartsOn: 1});

    const calendarDays = useMemo(() => {
        return eachDayOfInterval({start: calendarStart, end: calendarEnd});
    }, [calendarStart.getTime(), calendarEnd.getTime()]);

    // Group into weeks (rows of 7)
    const weeks: Date[][] = useMemo(() => {
        const result: Date[][] = [];
        for (let i = 0; i < calendarDays.length; i += 7) {
            result.push(calendarDays.slice(i, i + 7));
        }
        return result;
    }, [calendarDays]);

    // Lessons for selected day
    const selectedDayLessons = useMemo(() => {
        if (!selectedDay) return [];
        return getLessonsForDay(lessons, selectedDay);
    }, [lessons, selectedDay]);

    // Helper: does a day have any lessons?
    const dayHasLessons = (day: Date): boolean => {
        return lessons.some((item) => isSameDay(new Date(item.courseLesson.startDateTime), day));
    };

    // Helper: is the user assigned to at least one lesson on this day?
    const dayHasAssignment = (day: Date): boolean => {
        return lessons.some(
            (item) => isSameDay(new Date(item.courseLesson.startDateTime), day) && item.isAssignedToMe
        );
    };

    const handleMonthBack = () => onMonthChange(subMonths(monthDate, 1));
    const handleMonthForward = () => onMonthChange(addMonths(monthDate, 1));
    const handleToday = () => {
        onMonthChange(startOfMonth(new Date()));
        setSelectedDay(new Date());
    };

    return (
        <div>
            {/* Month Navigation */}
            <div className={styles.assistantCalendarNav}>
                <Button type="text" icon={<LeftOutlined />} onClick={handleMonthBack} />
                <span style={{fontWeight: 600, fontSize: 15}}>
                    {formatDate(monthDate, "LLLL yyyy")}
                </span>
                <Button type="text" icon={<RightOutlined />} onClick={handleMonthForward} />
                <Button size="small" onClick={handleToday}>
                    {"Heute"}
                </Button>
            </div>

            {/* Day-of-week headers */}
            <div className={styles.assistantMonthDayNames}>
                {DAY_NAMES.map((name) => (
                    <div key={name} className={styles.assistantMonthDayName}>
                        {name}
                    </div>
                ))}
            </div>

            {/* Month Grid */}
            <div className={styles.assistantMonthGrid}>
                {weeks.map((week, weekIdx) => (
                    <div key={weekIdx} className={styles.assistantMonthRow}>
                        {week.map((day) => {
                            const inMonth = isSameMonth(day, monthDate);
                            const isToday = isSameDay(day, today);
                            const isSelected = selectedDay && isSameDay(day, selectedDay);
                            const hasLessons = dayHasLessons(day);
                            const hasAssignment = hasLessons && dayHasAssignment(day);

                            const cellClasses = [
                                styles.assistantMonthCell,
                                !inMonth ? styles.assistantMonthCellOutside : "",
                                isToday ? styles.assistantMonthCellToday : "",
                                isSelected ? styles.assistantMonthCellSelected : "",
                            ]
                                .filter(Boolean)
                                .join(" ");

                            const dotClasses = [
                                styles.assistantMonthDot,
                                hasAssignment ? styles.assistantMonthDotAssigned : "",
                            ]
                                .filter(Boolean)
                                .join(" ");

                            return (
                                <div
                                    key={day.toISOString()}
                                    className={cellClasses}
                                    onClick={() => setSelectedDay(day)}
                                >
                                    <span>{getDate(day)}</span>
                                    {hasLessons ? <div className={dotClasses} /> : null}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Selected Day Lessons */}
            {selectedDay ? (
                <div className={styles.assistantMonthLessonList}>
                    <div className={styles.assistantMonthLessonHeader}>
                        {formatDate(selectedDay, "EEEE, d. MMMM yyyy")}
                    </div>
                    {selectedDayLessons.length === 0 ? (
                        <div className={styles.assistantEmpty}>{"Keine Dienste an diesem Tag."}</div>
                    ) : (
                        selectedDayLessons.map((item) => {
                            const lesson = item.courseLesson;
                            const courseLabel = [
                                lesson.course?.courseType?.name,
                                lesson.course?.prefixedCourseNumber,
                            ]
                                .filter(Boolean)
                                .join(" \u00B7 ");
                            const locationLabel = [lesson.location?.name, lesson.location?.city]
                                .filter(Boolean)
                                .join(", ");
                            const maxSlots =
                                item.assistantSlotsMax && item.assistantSlotsMax > 0
                                    ? item.assistantSlotsMax
                                    : null;
                            const isFull = maxSlots !== null && item.assistantSlotsUsed >= maxSlots;
                            const isDisabled = !item.isAssignedToMe && isFull;

                            const cardClasses = [
                                styles.assistantTimelineCard,
                                item.isAssignedToMe
                                    ? styles.assistantTimelineCardAssigned
                                    : "",
                                isFull && !item.isAssignedToMe
                                    ? styles.assistantTimelineCardFull
                                    : "",
                            ]
                                .filter(Boolean)
                                .join(" ");

                            return (
                                <div key={lesson.id} className={styles.assistantCalendarLessonItem}>
                                    <div className={cardClasses}>
                                        <div className={styles.assistantTimelineCardInfo}>
                                            <div className={styles.assistantTimelineCardTitle}>
                                                {courseLabel || "Kurseinheit"}
                                            </div>
                                            <div className={styles.assistantTimelineCardTime}>
                                                {formatTimeRange(
                                                    lesson.startDateTime,
                                                    lesson.endDateTime
                                                )}
                                                {locationLabel ? " \u00B7 " + locationLabel : ""}
                                            </div>
                                        </div>
                                        <div className={styles.assistantTimelineCardAction}>
                                            {isFull && !item.isAssignedToMe ? (
                                                <span
                                                    className={
                                                        styles.assistantTimelineCardFullLabel
                                                    }
                                                >
                                                    {"voll"}
                                                </span>
                                            ) : (
                                                <Button
                                                    type={item.isAssignedToMe ? "default" : "primary"}
                                                    size="small"
                                                    disabled={isDisabled}
                                                    loading={pendingLessonId === lesson.id}
                                                    className={item.isAssignedToMe ? styles.assistantBtnUnassign : undefined}
                                                    style={{borderRadius: 6}}
                                                    onClick={() => {
                                                        if (item.isAssignedToMe) {
                                                            onUnassign(lesson.id);
                                                        } else {
                                                            onAssign(lesson.id);
                                                        }
                                                    }}
                                                >
                                                    {item.isAssignedToMe ? "Austragen" : "Eintragen"}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default AssistantMonthCalendar;
